import { create } from 'zustand';
import { GameState, GameMode, Difficulty, ProvinceBlock, Position } from '@/types';
import { PROVINCES_DATA, DIFFICULTY_CONFIG, SCORING_CONFIG } from '@/data/provinces';
import { getRandomQuestion } from '@/data/quiz';
import { fetchChinaProvincesData, isPointInGeometry, calculateDistance, getProvinceCenter, initializeProvincesData } from '@/services/mapService';
import { preloadProvinceSVGs, getSVGCacheStatus } from '@/services/svgService';
import { getProvinceTolerance } from '@/config/provinceSizes';
import { getCurrentQuizConfig } from '@/config/quizConfig';

// 缩放配置常量
const ZOOM_CONFIG = {
  min: 0.5,
  max: 3.0,
  default: 1.2,
  step: 0.2,
  defaultCenter: [104, 35] as [number, number]
};

interface GameStore extends GameState {
  // 省份数据
  provinces: ProvinceBlock[];
  mapData: any;
  
  // 显示设置
  showProvinceNames: boolean;
  showAbbreviations: boolean;        // 显示简称拼音
  
  // 地图缩放状态
  mapZoom: number;
  mapCenter: [number, number];
  isMapDragging: boolean;
  
  // 强制重新渲染标识
  renderKey: number;
  
  // 游戏控制方法
  startGame: (mode: GameMode, difficulty: Difficulty) => void;
  pauseGame: () => void;
  resumeGame: () => void;
  resetGame: () => void;
  continueChallenge: () => void;
  backToHome: () => void;
  
  // 显示控制方法
  toggleProvinceNames: () => void;
  setShowProvinceNames: (show: boolean) => void;
  toggleAbbreviations: () => void;
  setShowAbbreviations: (show: boolean) => void;
  
  // 地图缩放控制方法
  zoomIn: () => void;
  zoomOut: () => void;
  resetMapView: () => void;
  setMapZoom: (zoom: number) => void;
  setMapCenter: (center: [number, number]) => void;
  setMapDragging: (dragging: boolean) => void;
  
  // 拼图操作
  moveProvince: (provinceId: string, position: Position) => void;
  placeProvince: (provinceId: string, position: Position) => boolean;
  useHint: () => string | null;
  
  // 问答系统
  triggerQuiz: () => void;
  answerQuiz: (answerIndex: number, timeUsed?: number) => boolean;
  skipQuiz: () => void;
  nextQuiz: () => void;
  finishQuizRound: () => void;
  getQuizProgress: () => { current: number; total: number };
  skipQuiz: () => void;
  
  // 计时器
  updateTimer: () => void;
  
  // 分数系统
  addScore: (points: number) => void;
  
  // 工具方法
  getCompletionRate: () => number;
  isProvinceNearCorrectPosition: (provinceId: string, position: Position) => boolean;
  getProvinceGeoData: (provinceId: string) => any;
  loadMapData: () => Promise<void>;
  
  // SVG相关方法
  preloadSVGs: () => Promise<void>;
  getSVGStatus: () => { total: number; loaded: number; failed: number };
}

const initialState: GameState = {
  mode: 'free',
  difficulty: 'easy',
  score: 0,
  timeRemaining: 0,
  totalTime: 0,
  completedProvinces: [],
  hintsUsed: 0,
  maxHints: 3,
  currentQuiz: undefined,
  quizQueue: [],
  currentQuizIndex: 0,
  quizResults: [],
  isInQuizRound: false,
  isGameStarted: false,
  isGameCompleted: false,
  isPaused: false,
};

// 随机选择省份
const getRandomProvinces = (difficulty: Difficulty): string[] => {
  // 排除澳门特别行政区（地图太小，拼图困难）
  const excludedProvinces = ['澳门特别行政区','香港特别行政区'];
  const allProvinceIds = PROVINCES_DATA
    .filter(p => !excludedProvinces.includes(p.name))
    .map(p => p.id);
  
  // 如果没有省份数据，返回空数组
  if (allProvinceIds.length === 0) {
    console.warn('警告：省份数据为空，无法开始游戏');
    return [];
  }
  
  const config = DIFFICULTY_CONFIG[difficulty];
  
  console.log(`🎯 随机选择省份，排除: ${excludedProvinces.join(', ')}`);
  
  // 如果是困难模式或者配置的数量为0，返回所有省份（除了排除的）
  if (difficulty === 'hard' || config.count === 0 || config.count >= allProvinceIds.length) {
    return [...allProvinceIds].sort(() => Math.random() - 0.5); // 随机排序
  }
  
  // 随机选择指定数量的省份
  const shuffled = [...allProvinceIds].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, config.count);
};

// 初始化省份数据
const initializeProvinces = (difficulty: Difficulty): ProvinceBlock[] => {
  // 随机选择省份
  const selectedProvinceIds = getRandomProvinces(difficulty);
  const selectedProvinces = PROVINCES_DATA.filter(p => selectedProvinceIds.includes(p.id));
  
  console.log(`初始化${difficulty}难度游戏，随机选择的省份:`, selectedProvinces.map(p => p.name));
  
  return selectedProvinces.map((province, index) => ({
    ...province,
    currentPosition: {
      // 在右侧区域随机分布
      x: 800 + (index % 3) * 100 + Math.random() * 50,
      y: 100 + Math.floor(index / 3) * 120 + Math.random() * 50
    },
    isPlaced: false,
    isLocked: false
  }));
};

// 获取下一个难度等级
const getNextDifficulty = (currentDifficulty: Difficulty): Difficulty => {
  const difficultyOrder: Difficulty[] = ['easy', 'medium', 'hard'];
  const currentIndex = difficultyOrder.indexOf(currentDifficulty);
  return difficultyOrder[(currentIndex + 1) % difficultyOrder.length];
};

// 检查省份是否在正确区域内（基于真实GeoJSON数据）
const isProvinceInCorrectArea = (provinceId: string, position: Position, mapData: any): boolean => {
  if (!mapData || !mapData.features) return false;
  
  // 查找对应的省份
  const province = PROVINCES_DATA.find(p => p.id === provinceId);
  if (!province) return false;
  
  // 查找对应的地图特征
  const targetFeature = mapData.features.find((feature: any) => 
    feature.properties.name === province.name
  );
  
  if (!targetFeature || !targetFeature.geometry) {
    return false;
  }
  
  try {
    // 检查点是否在几何体内
    const isInside = isPointInGeometry([position.x, position.y], targetFeature.geometry);
    
    if (isInside) return true;
    
    // 如果不在内部，检查是否接近中心点（容错处理）
    const center = getProvinceCenter(targetFeature);
    const distance = calculateDistance([position.x, position.y], center);
    const tolerance = getProvinceTolerance(provinceId);
    
    return distance <= tolerance;
  } catch (error) {
    console.error('检查省份位置时出错:', error);
    return false;
  }
};

// 根据省份大小获取容错范围
const getProvinceTolerance = (provinceId: string): number => {
  const largeProvincies = ['xinjiang', 'tibet', 'neimenggu', 'qinghai', 'sichuan', 'gansu'];
  const mediumProvincies = ['yunnan', 'guangxi', 'hunan', 'hubei', 'hebei', 'heilongjiang'];
  
  if (largeProvincies.includes(provinceId)) return 3.0;
  if (mediumProvincies.includes(provinceId)) return 2.0;
  return 1.5; // 小省份
};

export const useGameStore = create<GameStore>((set, get) => ({
  ...initialState,
  provinces: [],
  mapData: null,
  showProvinceNames: false, // 添加显示省名状态
  showAbbreviations: true,  // 默认显示简称拼音
  
  // 地图缩放状态初始化
  mapZoom: ZOOM_CONFIG.default,
  mapCenter: ZOOM_CONFIG.defaultCenter,
  isMapDragging: false,
  
  // 强制重新渲染标识
  renderKey: 0,

  loadMapData: async () => {
    try {
      console.log('游戏状态管理器：开始加载地图数据...');
      
      // 加载地图数据
      const mapData = await fetchChinaProvincesData();
      
      // 初始化省份数据
      const provincesData = await initializeProvincesData();
      
      if (provincesData && provincesData.length > 0) {
        console.log(`游戏状态管理器：成功加载 ${provincesData.length} 个省份数据`);
        set({ mapData });
      } else {
        console.warn('游戏状态管理器：省份数据加载失败，使用默认数据');
        set({ mapData });
      }
      
      console.log('游戏状态管理器：地图数据加载完成');
    } catch (error) {
      console.error('游戏状态管理器：加载地图数据失败:', error);
    }
  },

  startGame: (mode: GameMode, difficulty: Difficulty) => {
    // 检查省份数据是否已加载
    if (PROVINCES_DATA.length === 0) {
      console.error('无法开始游戏：省份数据未加载');
      return;
    }
    
    const config = DIFFICULTY_CONFIG[difficulty];
    const provinces = initializeProvinces(difficulty);
    
    if (provinces.length === 0) {
      console.error('无法开始游戏：没有可用的省份数据');
      return;
    }
    
    console.log(`开始${mode}模式，${difficulty}难度游戏，包含${provinces.length}个省份`);
    
    set({
      mode,
      difficulty,
      provinces,
      score: 0,
      timeRemaining: mode === 'free' ? 0 : config.timeLimit,
      totalTime: mode === 'free' ? 0 : config.timeLimit,
      completedProvinces: [],
      hintsUsed: 0,
      maxHints: config.hintsAllowed,
      currentQuiz: undefined,
      isGameStarted: true,
      isGameCompleted: false,
      isPaused: false,
    });

    // 异步预加载SVG文件
    setTimeout(() => {
      get().preloadSVGs();
    }, 100);
  },

  pauseGame: () => {
    set({ isPaused: true });
  },

  resumeGame: () => {
    set({ isPaused: false });
  },

  resetGame: () => {
    console.log('🔄 重置游戏状态...');
    const currentState = get();
    
    // 一次性重置所有状态
    set({
      ...initialState,
      provinces: [],
      mapData: currentState.mapData, // 保留已加载的地图数据
      showProvinceNames: currentState.showProvinceNames, // 保留省名显示设置
      showAbbreviations: currentState.showAbbreviations, // 保留简称拼音显示设置
      mapZoom: currentState.mapZoom, // 保留地图缩放设置
      mapCenter: currentState.mapCenter, // 保留地图中心设置
      isMapDragging: false,
      renderKey: currentState.renderKey + 1 // 更新渲染键
    });
    
    console.log('✅ 游戏状态已重置到初始状态');
  },

  continueChallenge: () => {
    const { mode, difficulty } = get();
    
    // 升级到下一个难度等级
    const nextDifficulty = getNextDifficulty(difficulty);
    
    // 开始新的挑战
    const config = DIFFICULTY_CONFIG[nextDifficulty];
    const provinces = initializeProvinces(nextDifficulty);
    
    console.log(`继续挑战：升级到${nextDifficulty}难度，随机选择${provinces.length}个省份`);
    
    set({
      difficulty: nextDifficulty,
      provinces,
      score: 0, // 重置分数
      timeRemaining: mode === 'free' ? 0 : config.timeLimit,
      totalTime: mode === 'free' ? 0 : config.timeLimit,
      completedProvinces: [],
      hintsUsed: 0,
      maxHints: config.hintsAllowed,
      currentQuiz: undefined,
      isGameStarted: true,
      isGameCompleted: false,
      isPaused: false,
    });
  },

  backToHome: () => {
    console.log('🏠 返回首页...');
    const currentState = get();
    console.log('当前状态:', {
      isGameStarted: currentState.isGameStarted,
      isGameCompleted: currentState.isGameCompleted,
      provinces: currentState.provinces.length,
      completedProvinces: currentState.completedProvinces.length
    });
    
    set({
      ...initialState,
      provinces: [],
      mapData: currentState.mapData, // 保留已加载的地图数据
      showProvinceNames: currentState.showProvinceNames, // 保留省名显示设置
      showAbbreviations: currentState.showAbbreviations, // 保留简称拼音显示设置
      mapZoom: currentState.mapZoom, // 保留地图缩放设置
      mapCenter: currentState.mapCenter, // 保留地图中心设置
      isMapDragging: false
    });
    
    console.log('✅ 已返回首页');
    console.log('返回后状态:', {
      isGameStarted: false,
      isGameCompleted: false,
      provinces: 0,
      completedProvinces: 0
    });
  },

  moveProvince: (provinceId: string, position: Position) => {
    const { provinces } = get();
    const updatedProvinces = provinces.map(province => 
      province.id === provinceId 
        ? { ...province, currentPosition: position }
        : province
    );
    
    set({ provinces: updatedProvinces });
  },

  placeProvince: (provinceId: string, position: Position) => {
    const { provinces, completedProvinces, mode, mapData } = get();
    const province = provinces.find(p => p.id === provinceId);
    
    if (!province || province.isLocked) return false;
    
    // 检查是否在正确位置
    const isCorrect = isProvinceInCorrectArea(provinceId, position, mapData);
    
    if (isCorrect) {
      // 正确放置 - 获取省份的真实中心点
      let correctPos = position;
      
      if (mapData) {
        const targetFeature = mapData.features.find((feature: any) => 
          feature.properties.name === province.name
        );
        
        if (targetFeature) {
          const center = getProvinceCenter(targetFeature);
          correctPos = { x: center[0], y: center[1] };
        }
      }

      const updatedProvinces = provinces.map(p => 
        p.id === provinceId 
          ? { 
              ...p, 
              currentPosition: correctPos, 
              isPlaced: true, 
              isLocked: true 
            }
          : p
      );
      
      const newCompletedProvinces = [...completedProvinces, provinceId];
      const consecutiveBonus = completedProvinces.length > 0 ? SCORING_CONFIG.consecutiveBonus : 0;
      const scoreIncrease = SCORING_CONFIG.correctPlacement + consecutiveBonus;
      
      console.log(`省份${province.name}拼接成功，得分+${scoreIncrease}`);
      
      set(state => ({
        provinces: updatedProvinces,
        completedProvinces: newCompletedProvinces,
        score: state.score + scoreIncrease,
        isGameCompleted: newCompletedProvinces.length === updatedProvinces.length
      }));
      console.log(`当前完成省份数量: ${newCompletedProvinces.length}`);
      // 根据配置触发问答
      const config = getCurrentQuizConfig();
      if (config.enabledModes.includes(mode as any) && 
          newCompletedProvinces.length % config.triggerInterval === 0) {
        get().triggerQuiz();
      }
      
      return true;
    } else {
      // 错误放置
      console.log(`省份${province.name}放置错误，扣分${Math.abs(SCORING_CONFIG.wrongPlacement)}`);
      set(state => ({
        score: Math.max(0, state.score + SCORING_CONFIG.wrongPlacement)
      }));
      return false;
    }
  },

  useHint: () => {
    const { provinces, hintsUsed, maxHints } = get();
    
    if (hintsUsed >= maxHints) return null;
    
    // 找到一个未完成的省份
    const uncompletedProvinces = provinces.filter(p => !p.isPlaced);
    if (uncompletedProvinces.length === 0) return null;
    
    const randomProvince = uncompletedProvinces[Math.floor(Math.random() * uncompletedProvinces.length)];
    
    console.log(`使用提示：${randomProvince.name}`);
    
    set(state => ({
      hintsUsed: state.hintsUsed + 1,
      score: Math.max(0, state.score + SCORING_CONFIG.hintPenalty)
    }));
    
    return randomProvince.id;
  },

  triggerQuiz: () => {
    const { completedProvinces, isInQuizRound, mode } = get();
    const config = getCurrentQuizConfig();
    
    if (completedProvinces.length === 0 || isInQuizRound) return;
    
    // 检查当前模式是否启用问答
    if (!config.enabledModes.includes(mode as any)) {
      console.log(`🚫 当前模式 ${mode} 未启用问答功能`);
      return;
    }
    
    console.log(`🧠 触发问答轮次，准备${config.questionsPerRound}道题目`);
    
    // 生成问答队列
    const quizQueue = [];
    const usedProvinces = new Set<string>();
    
    for (let i = 0; i < config.questionsPerRound; i++) {
      let availableProvinces = completedProvinces;
      
      // 如果启用了避免重复省份选项
      if (config.avoidRepeatProvinces) {
        availableProvinces = completedProvinces.filter(id => !usedProvinces.has(id));
        
        if (availableProvinces.length === 0) {
          // 如果所有省份都用过了，重新开始
          usedProvinces.clear();
          availableProvinces = [...completedProvinces];
        }
      }
      
      // 如果优先选择最近完成的省份
      if (config.preferRecentProvinces && availableProvinces.length > 1) {
        // 取最后完成的几个省份
        const recentCount = Math.min(3, availableProvinces.length);
        availableProvinces = availableProvinces.slice(-recentCount);
      }
      
      const randomProvinceId = availableProvinces[Math.floor(Math.random() * availableProvinces.length)];
      const question = getRandomQuestion(randomProvinceId);
      
      if (question) {
        quizQueue.push(question);
        usedProvinces.add(randomProvinceId);
        console.log(`📝 添加题目 ${i + 1}/${config.questionsPerRound}: ${question.question}`);
      }
    }
    
    if (quizQueue.length > 0) {
      set({
        quizQueue,
        currentQuizIndex: 0,
        currentQuiz: quizQueue[0],
        quizResults: [],
        isInQuizRound: true
      });
      console.log(`✅ 问答轮次开始，共${quizQueue.length}道题目`);
    }
  },

  answerQuiz: (answerIndex: number, timeUsed: number = 0) => {
    const { currentQuiz, quizResults } = get();
    if (!currentQuiz) return false;
    
    const config = getCurrentQuizConfig();
    const isCorrect = answerIndex === currentQuiz.correctAnswer;
    
    // 记录答题结果
    const result = {
      questionId: currentQuiz.id,
      isCorrect,
      selectedAnswer: answerIndex,
      correctAnswer: currentQuiz.correctAnswer,
      timeUsed,
      timestamp: Date.now()
    };
    
    const newQuizResults = [...quizResults, result];
    
    if (isCorrect) {
      // 计算得分（包含时间奖励）
      let score = config.correctScore;
      
      if (config.timeBonus && timeUsed > 0) {
        const timeRemaining = config.timePerQuestion - timeUsed;
        const timeRatio = Math.max(0, timeRemaining / config.timePerQuestion);
        const bonus = Math.round(score * timeRatio * (config.timeBonusMultiplier - 1));
        score += bonus;
        
        if (bonus > 0) {
          console.log(`✅ 问答正确，基础得分+${config.correctScore}，时间奖励+${bonus}，总计+${score}`);
        } else {
          console.log(`✅ 问答正确，得分+${score}`);
        }
      } else {
        console.log(`✅ 问答正确，得分+${score}`);
      }
      
      set(state => ({
        score: state.score + score,
        quizResults: newQuizResults
      }));
    } else {
      console.log(`❌ 问答错误，正确答案是: ${currentQuiz.options[currentQuiz.correctAnswer]}`);
      set({ quizResults: newQuizResults });
    }
    
    return isCorrect;
  },

  skipQuiz: () => {
    const { currentQuiz, quizResults } = get();
    if (!currentQuiz) return;
    
    // 记录跳过的题目
    const result = {
      questionId: currentQuiz.id,
      isCorrect: false,
      selectedAnswer: -1, // -1 表示跳过
      correctAnswer: currentQuiz.correctAnswer,
      timeUsed: 0,
      timestamp: Date.now()
    };
    
    set(state => ({
      quizResults: [...state.quizResults, result]
    }));
    
    console.log('⏭️ 跳过当前题目');
  },

  nextQuiz: () => {
    const { quizQueue, currentQuizIndex } = get();
    const nextIndex = currentQuizIndex + 1;
    
    if (nextIndex < quizQueue.length) {
      // 还有下一题
      set({
        currentQuizIndex: nextIndex,
        currentQuiz: quizQueue[nextIndex]
      });
      console.log(`📝 进入第${nextIndex + 1}题，共${quizQueue.length}题`);
    } else {
      // 所有题目完成
      get().finishQuizRound();
    }
  },

  finishQuizRound: () => {
    const { quizResults } = get();
    const correctCount = quizResults.filter(r => r.isCorrect).length;
    const totalCount = quizResults.length;
    
    console.log(`🎯 问答轮次结束，正确率: ${correctCount}/${totalCount} (${Math.round(correctCount / totalCount * 100)}%)`);
    
    set({
      currentQuiz: undefined,
      quizQueue: [],
      currentQuizIndex: 0,
      isInQuizRound: false
    });
  },

  getQuizProgress: () => {
    const { currentQuizIndex, quizQueue } = get();
    return {
      current: currentQuizIndex + 1,
      total: quizQueue.length
    };
  },

  updateTimer: () => {
    const { timeRemaining, mode, isGameStarted, isPaused } = get();
    
    if (mode === 'free' || !isGameStarted || isPaused) return;
    
    if (timeRemaining > 0) {
      set({ timeRemaining: timeRemaining - 1 });
    } else {
      // 时间到，游戏结束
      console.log('时间到，游戏结束');
      set({ isGameCompleted: true });
    }
  },

  addScore: (points: number) => {
    set(state => ({
      score: Math.max(0, state.score + points)
    }));
  },

  getCompletionRate: () => {
    const { provinces, completedProvinces } = get();
    if (provinces.length === 0) return 0;
    return (completedProvinces.length / provinces.length) * 100;
  },

  isProvinceNearCorrectPosition: (provinceId: string, position: Position) => {
    const { mapData } = get();
    if (!mapData) return false;
    
    const province = PROVINCES_DATA.find(p => p.id === provinceId);
    if (!province) return false;
    
    const targetFeature = mapData.features.find((feature: any) => 
      feature.properties.name === province.name
    );
    
    if (!targetFeature) return false;
    
    try {
      const center = getProvinceCenter(targetFeature);
      const distance = calculateDistance([position.x, position.y], center);
      const tolerance = getProvinceTolerance(provinceId) * 1.5; // 提示范围更大
      
      return distance <= tolerance;
    } catch (error) {
      return false;
    }
  },

  getProvinceGeoData: (provinceId: string) => {
    const { mapData } = get();
    if (!mapData) return null;
    
    const province = PROVINCES_DATA.find(p => p.id === provinceId);
    if (!province) return null;
    
    return mapData.features.find((feature: any) => 
      feature.properties.name === province.name
    );
  },

  // SVG相关方法
  preloadSVGs: async () => {
    const { provinces } = get();
    const provinceNames = provinces.map(p => p.name);
    
    console.log(`🎨 开始预加载 ${provinceNames.length} 个省份的SVG文件`);
    
    try {
      await preloadProvinceSVGs(provinceNames);
      console.log(`✅ SVG文件预加载完成`);
    } catch (error) {
      console.error('❌ SVG预加载失败:', error);
    }
  },

  getSVGStatus: () => {
    return getSVGCacheStatus();
  },

  // 显示控制方法
  toggleProvinceNames: () => {
    set(state => ({
      showProvinceNames: !state.showProvinceNames
    }));
  },

  setShowProvinceNames: (show: boolean) => {
    set({ showProvinceNames: show });
  },

  toggleAbbreviations: () => {
    set(state => ({
      showAbbreviations: !state.showAbbreviations
    }));
  },

  setShowAbbreviations: (show: boolean) => {
    set({ showAbbreviations: show });
  },

  // 地图缩放控制方法
  zoomIn: () => {
    const { mapZoom } = get();
    const newZoom = Math.min(mapZoom + ZOOM_CONFIG.step, ZOOM_CONFIG.max);
    console.log(`🔍 地图放大: ${mapZoom.toFixed(1)} → ${newZoom.toFixed(1)}`);
    set({ mapZoom: newZoom });
  },

  zoomOut: () => {
    const { mapZoom } = get();
    const newZoom = Math.max(mapZoom - ZOOM_CONFIG.step, ZOOM_CONFIG.min);
    console.log(`🔍 地图缩小: ${mapZoom.toFixed(1)} → ${newZoom.toFixed(1)}`);
    set({ mapZoom: newZoom });
  },

  resetMapView: () => {
    const { mapZoom, mapCenter } = get();
    console.log(`🏠 恢复地图视图: 缩放 ${mapZoom.toFixed(1)} → ${ZOOM_CONFIG.default.toFixed(1)}, 中心 [${mapCenter.join(', ')}] → [${ZOOM_CONFIG.defaultCenter.join(', ')}]`);
    set({
      mapZoom: ZOOM_CONFIG.default,
      mapCenter: ZOOM_CONFIG.defaultCenter
    });
  },

  setMapZoom: (zoom: number) => {
    const clampedZoom = Math.max(ZOOM_CONFIG.min, Math.min(zoom, ZOOM_CONFIG.max));
    if (clampedZoom !== zoom) {
      console.warn(`🔍 缩放值被限制: ${zoom.toFixed(2)} → ${clampedZoom.toFixed(2)}`);
    }
    set({ mapZoom: clampedZoom });
  },

  setMapCenter: (center: [number, number]) => {
    set({ mapCenter: center });
  },

  setMapDragging: (dragging: boolean) => {
    set({ isMapDragging: dragging });
  }
}));
